import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InstallerLogo, appConfig } from "@/branding/Branding";
import { Download, FileText, Image as ImageIcon, Upload, Package } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { toast } from "sonner";

function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
}
function humanSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Documents vault — mirrors app/(customer)/documents.tsx.
 * Customer can upload files (PDF or image, ≤10MB). Installer-uploaded docs
 * appear here; each row streams the file via /documents/:id/download with the
 * bearer token appended as ?token= (browser cannot set headers on <a href>).
 */
export default function Documents() {
    const { user } = useAuth();
    const [projectId, setProjectId] = useState(null);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const token = getToken();

    const load = async (pid) => {
        try {
            setDocs(await api.listDocuments(pid));
        } catch {
            toast.error("Could not load documents");
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const p = user?.role === "installer"
                    ? (await api.listProjects())[0]
                    : await api.myProject();
                if (!p) throw new Error("No project");
                setProjectId(p.id);
                await load(p.id);
            } finally { setLoading(false); }
        })();
    }, [user]);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !projectId) return;
        setUploading(true);
        try {
            const saved = await api.uploadDocument(projectId, file, file.name, "Customer Upload");
            setDocs((prev) => [saved, ...prev]);
            toast.success("Uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const openDoc = async (doc) => {
        try {
            const { token: dt } = await api.documentDownloadToken(doc.id);
            window.open(`${api.documentDownloadUrl(doc.id)}?dt=${encodeURIComponent(dt)}`, "_blank");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };
    const downloadHandover = async () => {
        if (!projectId) return;
        try {
            const { token: dt } = await api.handoverDownloadToken(projectId);
            window.open(`${api.handoverPdfUrl(projectId)}?dt=${encodeURIComponent(dt)}`, "_blank");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Handover download failed");
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F5F7] pb-20" data-testid="documents-page">
            <PageContainer width="medium">
                <PageHeader
                    eyebrow="CUSTOMER"
                    title="Documents"
                    subtitle="Everything Pearl Energy has filed for your installation."
                    icon={FileText}
                    backFallback="/dashboard"
                    testid="documents"
                    rightSlot={(
                        <div className="flex items-center gap-2">
                            <button
                                onClick={downloadHandover}
                                className="pe-btn-outline text-sm h-10"
                                data-testid="download-handover-pdf"
                            >
                                <Package size={14} strokeWidth={1.75} /> Handover pack (PDF)
                            </button>
                            <input ref={fileRef} type="file" hidden accept="application/pdf,image/*" onChange={upload} data-testid="documents-file-input" />
                            <button
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="pe-btn-primary text-sm h-10 disabled:opacity-60"
                                data-testid="documents-upload-btn"
                            >
                                <Upload size={14} strokeWidth={1.75} /> {uploading ? "Uploading…" : "Upload"}
                            </button>
                        </div>
                    )}
                />

                <div className="mt-6 space-y-2" data-testid="documents-list">
                    {loading ? (
                        <div className="pe-card p-8 text-center text-sm text-[#4B5565]">Loading documents…</div>
                    ) : docs.length === 0 ? (
                        <div className="pe-card p-8 text-center text-sm text-[#4B5565]">No documents yet. Upload one to get started.</div>
                    ) : (
                        docs.map((d) => {
                            const isImage = (d.file_type || "").startsWith("image/");
                            return (
                                <div key={d.id} className="pe-card pe-card-hover p-4 md:p-5 flex items-center gap-4" data-testid={`doc-${d.id}`}>
                                    <span className="w-11 h-11 rounded-xl bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center shrink-0">
                                        {isImage ? <ImageIcon size={18} strokeWidth={1.75} /> : <FileText size={18} strokeWidth={1.75} />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-base text-[#131B2F] font-medium truncate">{d.title}</div>
                                        <div className="text-sm text-[#4B5565] truncate">
                                            {d.category} · {d.uploaded_by_name} · {formatDate(d.uploaded_at)}
                                            {d.file_size ? ` · ${humanSize(d.file_size)}` : ""}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => openDoc(d)}
                                        className="pe-btn-outline text-sm h-9"
                                        data-testid={`doc-open-${d.id}`}
                                    >
                                        <Download size={14} strokeWidth={1.75} /> Open
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                <p className="mt-4 text-xs text-[#4B5565]">PDF or image files up to 10&nbsp;MB. Customer uploads are visible to your installer.</p>
            </PageContainer>
        </div>
    );
}
